import { useState } from "react";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import Loader from "common/components/Loader";
import RenderIf from "common/components/RenderIf";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import useAllLabels from "common/datahooks/useAllLabels";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import AddLabelButton from "./AddLabelButton";
import LabelDrawer from "./LabelDrawer";
import LabelListItem from "./LabelListItem";

import type { LabelType } from "../../types";

import NoResultsImage from "assets/images/empty-placeholders/result-not-found.png";
import EmptyPlaceholderImage from "assets/images/empty-placeholders/templates.png";

export default function Labels({ type }: { type: LabelType }) {
  const isDesktop = useTwBreakpoint("lg");
  const [searchValue, setSearchValue] = useState("");
  const [selectedLabelId, setSelectedLabelId] = useState<string>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { allLabels, isLoadingAllLabels, isAllLabelsError, refetchAllLabels } =
    useAllLabels(type);

  if (isAllLabelsError)
    return (
      <ErrorPlaceholder
        errorMessage="Could not display labels"
        onRetry={refetchAllLabels}
      />
    );

  if (isLoadingAllLabels) return <Loader />;

  if (!allLabels.length)
    return (
      <EmptyPlaceholder
        imageSrc={EmptyPlaceholderImage}
        title="No labels found"
        subtitle={`Create a label to start organizing your ${type}s`}
        actionComponent={<AddLabelButton labelType={type} size="md" />}
      />
    );

  const filteredLabel = allLabels.filter(({ name }) =>
    searchValue ? name.toLowerCase().includes(searchValue.toLowerCase()) : true,
  );

  function clearSearch() {
    setSearchValue("");
  }

  const selectedLabel = allLabels.find(({ id }) => id === selectedLabelId);

  return (
    <>
      <SearchInput
        placeholder="Search labels"
        value={searchValue}
        className="md:max-w-80"
        variant={isDesktop ? "sm" : "lg"}
        onChange={({ target: { value } }) => setSearchValue(value)}
        onClear={clearSearch}
      />

      {/* List of labels */}
      {filteredLabel.length ? (
        <div className="grid grid-cols-1 gap-y-2.5 md:grid-cols-2 md:gap-3">
          {filteredLabel.map((label) => (
            <LabelListItem
              key={label.id}
              labelType={type}
              label={label}
              isEditing={selectedLabel && selectedLabel.id === label.id}
              onCancelEdit={() => setSelectedLabelId(null)}
              onStartEdit={() => {
                setSelectedLabelId(label.id);
                if (!isDesktop) {
                  setIsDrawerOpen(true);
                }
              }}
            />
          ))}
        </div>
      ) : (
        <EmptyPlaceholder
          imageSrc={NoResultsImage}
          title="No labels found"
          subtitle="No labels match your search criteria. Try adjusting your search"
          actionComponent={<Button onClick={clearSearch}>Clear search</Button>}
        />
      )}

      <RenderIf condition={!!selectedLabel && !isDesktop}>
        <LabelDrawer
          isOpen={isDrawerOpen}
          setIsOpen={setIsDrawerOpen}
          label={selectedLabel}
          labelType={type}
        />
      </RenderIf>
    </>
  );
}
